import { motion } from 'framer-motion';
import { CategoryValue } from 'shared/components/charts/types';
import { CHART_INK } from 'shared/constants/chartPalette';
import { GROW, STAGGER } from 'shared/components/motion/tokens';
import { useRevealOnce } from 'shared/components/motion/useRevealOnce';

interface MagnitudeBarListProps {
  items: CategoryValue[];
  color?: string;
  formatValue?: (value: number) => string;
  /** false para dados ordinais (faixas de dias, antecedência…), onde a ordem já é o dado. */
  sort?: boolean;
}

const defaultFormatValue = (value: number) => `R$ ${value.toLocaleString('pt-BR')}`;

export function MagnitudeBarList({
  items,
  color = CHART_INK.brand,
  formatValue = defaultFormatValue,
  sort = true,
}: MagnitudeBarListProps) {
  const [ref, inView] = useRevealOnce();
  const sorted = sort ? [...items].sort((a, b) => b.value - a.value) : items;
  const max = Math.max(...sorted.map((item) => item.value));

  return (
    <div ref={ref} className="flex flex-col gap-3">
      {sorted.map((item, index) => (
        <div key={item.id} className="group">
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="text-ink transition-colors group-hover:text-primary-dark">{item.label}</span>
            <span className="font-medium text-ink">{formatValue(item.value)}</span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-chip">
            <motion.div
              className="h-full rounded-full transition-[filter] group-hover:brightness-110"
              style={{ background: `linear-gradient(90deg, ${color}bb, ${color})` }}
              initial={{ width: 0 }}
              animate={{ width: inView ? `${(item.value / max) * 100}%` : 0 }}
              transition={{ ...GROW, delay: inView ? index * STAGGER : 0 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
